import { Box, Button, CardMedia, Skeleton, Typography } from '@mui/material'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import React from 'react'
import { useStyles } from './style'

const PROMOTE_FEATURE_COLORS = {
  bg: '#e4edf5',
  text: '#333'
}

/**
 * Category Promote Feature Card - A quiz filter card for category landing pages
 * Filters content to show only quizzes/assessments for the current category
 *
 * Quiz Filter Rules (same as FilterBar onQuizFilterClick):
 * - Handle quiz filter click - could navigate to quiz page or filter by quiz content type
 * - Filter to show only quiz/assessment content for the current category
 * - Apply content type filter for quizzes and assessments
 * - Update search results to display only quiz-related content
 */
export const CategoryPromoteFeatureCard = ({ dataTest, category, onQuizFilter, loading, ...props }) => {
  const classes = useStyles(PROMOTE_FEATURE_COLORS)

  const handleCtaClick = () => {
    if (onQuizFilter) {
      onQuizFilter(category)
    }
  }

  const categoryName = category?.name || 'this topic'
  const featureData = {
    title: `${categoryName} Insights`,
    description: `Discover your knowledge gaps and strengths in ${categoryName.toLowerCase()}.`,
    ctaText: 'See all Quizzes',
    imageUrl: 'https://nova-live.imgix.net/card-quizzes.jpg',
    altText: `${categoryName} Assessment Insights`
  }

  return (
    <Box className={classes.verticalBannerEdge} data-test={dataTest}>
      <Box className={classNames(classes.verticalBannerCtn, classes.verticalBannerCard)}>
        <Box>
          {loading
            ? (
              <Skeleton variant='rectangular' className={classes.verticalBannerImg} />
              )
            : (
              <CardMedia
                image={featureData.imageUrl}
                title={featureData.altText}
                className={classes.verticalBannerImg}
              />
              )}
        </Box>
        <Box className={classes.verticalBannerTxtCtn}>
          <Box>
            <Typography
              component='h3'
              className={classes.verticalBannerTitle}
              data-test={`${dataTest}-title`}
            >
              {loading
                ? <Skeleton width='80%' />
                : featureData.title}
            </Typography>
            <Typography
              variant='body2'
              className={classes.verticalBannerDescription}
              data-test={`${dataTest}-description`}
            >
              {loading
                ? (
                  <>
                    <Skeleton width='100%' />
                    <Skeleton width='70%' />
                  </>
                  )
                : featureData.description}
            </Typography>
          </Box>
          <Box>
            {loading
              ? (
                <Skeleton variant='rectangular' width='60%' height='36px' />
                )
              : (
                <Button
                  variant='contained'
                  color='primary'
                  onClick={handleCtaClick}
                  data-test={`${dataTest}-cta`}
                >
                  {featureData.ctaText}
                </Button>
                )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

CategoryPromoteFeatureCard.propTypes = {
  /** Test ID for the card component */
  dataTest: PropTypes.string,
  /** Category object containing name and other category data */
  category: PropTypes.shape({
    name: PropTypes.string
  }),
  /** Callback function to handle quiz filtering */
  onQuizFilter: PropTypes.func,
  /** Whether the card is in loading state */
  loading: PropTypes.bool
}

CategoryPromoteFeatureCard.defaultProps = {
  dataTest: 'category-promote-feature-card',
  category: { name: 'This Topic' },
  loading: false
}

export default CategoryPromoteFeatureCard
